import { colorSeasons } from "@/constants";

export function determineColorSeason(answers: Record<string, string>) {
  const undertone = answers.undertone;
  const hairColor = answers.hair;
  const eyeColor = answers.eyes;
  const contrast = answers.contrast;
  const tanning = answers.tanning;

  // Helper function to determine if a color is warm or cool
  const isWarmHair = (color: string) => {
    return ["warm_blonde", "red"].includes(color);
  };

  const isCoolHair = (color: string) => {
    return ["dark", "cool_blonde"].includes(color);
  };

  const isWarmEye = (color: string) => {
    return ["dark_brown", "light_brown"].includes(color);
  };

  const isCoolEye = (color: string) => {
    return ["blue", "green"].includes(color);
  };

  // Enhanced color temperature scoring
  const getColorTemperatureScore = () => {
    let score = 0;

    // Undertone contribution (most significant factor)
    if (undertone === "warm") score += 0.4;
    if (undertone === "cool") score -= 0.4;
    if (undertone === "neutral" || undertone === "not_sure") score += 0.1;

    // Hair color contribution
    if (isWarmHair(hairColor)) score += 0.3;
    if (isCoolHair(hairColor)) score -= 0.3;

    // Eye color contribution
    if (isWarmEye(eyeColor)) score += 0.2;
    if (isCoolEye(eyeColor)) score -= 0.2;

    // Tanning behavior as a minor factor
    if (tanning === "always_tans") score += 0.1;
    if (tanning === "burns") score -= 0.1;

    return Math.max(-1, Math.min(1, score));
  };

  const getContrastScore = () => {
    if (contrast === "high") return 1;
    if (contrast === "medium") return 0.5;
    return 0;
  };

  const temperatureScore = getColorTemperatureScore();
  const contrastScore = getContrastScore();

  let primarySeason: "WINTER" | "SPRING" | "SUMMER" | "AUTUMN";
  let subSeason: string;

  try {
    if (temperatureScore > 0.2) {
      primarySeason = contrastScore > 0.6 ? "SPRING" : "AUTUMN";
    } else if (temperatureScore < -0.2) {
      primarySeason = contrastScore > 0.6 ? "WINTER" : "SUMMER";
    } else {
      if (contrastScore > 0.6) {
        primarySeason = "WINTER";
      } else if (contrastScore < 0.4) {
        primarySeason = "SUMMER";
      } else {
        primarySeason = tanning === "always_tans" || temperatureScore > 0 ? "SPRING" : "SUMMER";
      }
    }

    // Determine subseason
    if (primarySeason === "WINTER") {
      subSeason = "TRUE";
    } else if (primarySeason === "SPRING") {
      subSeason = "TRUE";
    } else if (primarySeason === "SUMMER") {
      subSeason = "TRUE";
    } else {
      if (contrastScore < 0.4) subSeason = "SOFT";
      else subSeason = "DEEP";
    }

    // Validation check
    const seasonData = colorSeasons[primarySeason]?.subseasons[subSeason];
    if (!seasonData) {
      if (primarySeason === "AUTUMN") subSeason = "DEEP";
      else subSeason = "TRUE";
    }

    return { season: primarySeason, subSeason };
  } catch (error) {
    console.error("Error in color season determination:", error);
    return { season: "SUMMER", subSeason: "TRUE" };
  }
}
